import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MatchState,
  TeamState,
  Player,
  Rally,
  SetRecord,
  ValidationError,
  MATCH_CONFIG,
  CategoryAssignment,
  Timeout,
  getMatchRules,
  MatchRulesConfig,
} from '@/types';
import SetWonBanner from '@/components/modals/SetWonBanner';
import ServerSelectionModal from '@/components/modals/ServerSelectionModal';
import VolleyballCategoryModal from '@/components/modals/VolleyballCategoryModal';
import ErrorPopup from '@/components/modals/ErrorPopup';
import RallyHistoryModal from '@/components/modals/RallyHistoryModal';
import UndoConfirmPopup from '@/components/modals/UndoConfirmPopup';
import TimeoutModal, { TimeoutCompletedDisplay } from '@/components/modals/TimeoutModal';
import SideChangeNotice from '@/components/modals/SideChangeNotice';
import AppHeader from '@/components/AppHeader';
import { getTournament } from '@/lib/supabase';

// Helper function to get match rules
function getMatchRulesConfigLocal(match: MatchState['match'], tournamentRules?: MatchState['tournamentRules']): MatchRulesConfig | null {
  if (!match) return null;
  return getMatchRules(match.matchType, tournamentRules || null, match.winningPoint, match.goldenPoint);
}

interface LiveScoringProps {
  matchState: MatchState;
  servingTeam: TeamState | null;
  currentServer: Player | null;
  eligibleServers: { eligible: Player[]; ineligible: { player: Player; remaining: number }[] };
  serverSelectionNeeded: boolean;
  onRecordRally: (
    winningTeamId: string,
    categories?: CategoryAssignment[],
    isServerFault?: boolean
  ) => Promise<void>;
  onSelectNextServer: (player: Player) => Promise<void>;
  onUndo: () => Promise<void>;
  onUndoServerSelection: () => Promise<void>;
  canUndoServerSelection: () => { canUndo: boolean; servingTeamId: string | null };
  onStartNewSet: (firstServerId: string, firstServerTeamId?: string) => Promise<void>;
  onRecordServeFoul: (server: Player) => Promise<void>;
  onRecordServerFault: () => Promise<void>;
  onFetchSetRallies: (setId: string) => Promise<Rally[]>;
  onSignOut: () => void;
  tournamentId?: string;
  onExitToTournament?: (tournamentId: string) => void;
  readOnly?: boolean;
  onRefresh?: () => Promise<void>;
  lastRefreshed?: Date | null;
  // Timeout props
  activeTimeout?: Timeout | null;
  onStartTimeout?: (teamId: string) => Promise<void>;
  onEndTimeout?: () => Promise<void>;
  completedTimeouts?: Timeout[];
  lastTimeoutDuration?: number | null;
  // Side change props
  showSideChangeNotice?: boolean;
  onConfirmSideChange?: () => void;
  // First server selection for new sets
  onSelectFirstServerTeam?: (teamId: string) => void;
}

export default function LiveScoring({
  matchState,
  servingTeam,
  currentServer,
  eligibleServers,
  serverSelectionNeeded,
  onRecordRally,
  onSelectNextServer,
  onUndo,
  onUndoServerSelection,
  canUndoServerSelection,
  onStartNewSet,
  onRecordServeFoul,
  onRecordServerFault,
  onFetchSetRallies,
  onSignOut,
  tournamentId,
  onExitToTournament,
  readOnly = false,
  onRefresh,
  lastRefreshed,
  activeTimeout,
  onStartTimeout,
  onEndTimeout,
  completedTimeouts = [],
  lastTimeoutDuration,
  showSideChangeNotice,
  onConfirmSideChange,
  onSelectFirstServerTeam,
}: LiveScoringProps) {
  const navigate = useNavigate();
  const [showVolleyballCategories, setShowVolleyballCategories] = useState(false);
  // Auto-open server selection if recovery landed mid-service-break (no server assigned yet)
  // Never auto-open for read-only viewers
  const [showServerSelection, setShowServerSelection] = useState(readOnly ? false : serverSelectionNeeded);
  const [pendingTeamWonId, setPendingTeamWonId] = useState<string | null>(null);
  const [error, setError] = useState<ValidationError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [historyModal, setHistoryModal] = useState<{
    setNumber: number;
    setRecord: SetRecord;
    rallies: Rally[];
  } | null>(null);
  const [historyLoading, setHistoryLoading] = useState<string | null>(null);
  const [showUndoConfirm, setShowUndoConfirm] = useState(false);
  const [undoType, setUndoType] = useState<'rally' | 'server'>('rally');
  const [tournamentName, setTournamentName] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // ── Prevent accidental refresh / navigation for scorers ──────────────────
  useEffect(() => {
    if (readOnly) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = 'Match in progress — are you sure you want to leave?';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [readOnly]);

  // Block F5 / Ctrl+R / Cmd+R hard refresh while scoring
  useEffect(() => {
    if (readOnly) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      const isRefresh =
        e.key === 'F5' ||
        ((e.ctrlKey || e.metaKey) && e.key === 'r');
      if (isRefresh) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [readOnly]);

  // ── Fullscreen API ─────────────────────────────────────────────────────────
  const toggleFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen({ navigationUI: 'hide' });
      } else {
        await document.exitFullscreen();
      }
    } catch {
      // Fullscreen not supported / denied — silently ignore
    }
  }, []);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // Open server selection modal when server selection is needed (e.g., after server fault)
  useEffect(() => {
    if (serverSelectionNeeded && !showServerSelection && !readOnly) {
      setShowServerSelection(true);
    }
  }, [serverSelectionNeeded, showServerSelection, readOnly]);

  // Close server selection modal when server selection is no longer needed
  // (e.g., after first server is selected via SetWonBanner flow)
  useEffect(() => {
    if (!serverSelectionNeeded && showServerSelection) {
      setShowServerSelection(false);
    }
  }, [serverSelectionNeeded, showServerSelection]);

  const handleManualRefresh = async () => {
    if (!onRefresh || refreshing) return;
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  const formatTime = (date: Date) =>
    date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  // Load tournament name if match belongs to a tournament
  useEffect(() => {
    const tid = matchState.match?.tournamentId;
    if (!tid) { setTournamentName(null); return; }
    getTournament(tid)
      .then(t => setTournamentName(t.name))
      .catch(() => setTournamentName(null));
  }, [matchState.match?.tournamentId]);

  // Check if set was just won
  const showSetWonBanner = matchState.status === 'set-won' && matchState.currentSet;

  // Serving team's score is displayed first during active scoring.
  const servingFirst = !!(matchState.teamA && matchState.teamB && servingTeam && servingTeam.id === matchState.teamB.id);
  const firstTeam = servingFirst ? matchState.teamB! : matchState.teamA!;
  const secondTeam = servingFirst ? matchState.teamA! : matchState.teamB!;
  const firstScore = matchState.currentSet ? (servingFirst ? matchState.currentSet.scoreB : matchState.currentSet.scoreA) : 0;
  const secondScore = matchState.currentSet ? (servingFirst ? matchState.currentSet.scoreA : matchState.currentSet.scoreB) : 0;

  // Derive rotation dots
  const rotationDots = useMemo(() => {
    if (!servingTeam) return [];
    const playersServed = new Set<string>();
    const dots: { filled: boolean; isCurrent: boolean }[] = [];

    // Go through serve history up to the actual team size
    for (let i = 0; i < servingTeam.players.length; i++) {
      const playerId = servingTeam.rotationState.serveHistory[i];
      if (playerId) {
        playersServed.add(playerId);
        const isCurrent = playerId === currentServer?.id;
        dots.push({ filled: true, isCurrent });
      } else {
        dots.push({ filled: false, isCurrent: false });
      }
    }

    return dots;
  }, [servingTeam, currentServer]);

  // Get the team that lost the rally
  const losingTeam = useMemo(() => {
    if (!pendingTeamWonId || !matchState.teamA || !matchState.teamB) return null;
    return pendingTeamWonId === matchState.teamA.id ? matchState.teamB : matchState.teamA;
  }, [pendingTeamWonId, matchState.teamA, matchState.teamB]);

  // Handle team button click - now directly opens category modal
  const handleTeamWonClick = (teamId: string) => {
    if (isSubmitting) return;
    setPendingTeamWonId(teamId);
    // Skip lost reason selection - go directly to volleyball categories
    setShowVolleyballCategories(true);
  };

  // Handle volleyball category confirmation
  const handleCategoryConfirm = async (categories: CategoryAssignment[], isServerFault?: boolean) => {
    setShowVolleyballCategories(false);

    if (isServerFault) {
      // Server Fault: winningTeam gets point, service transfers
      await commitServerFault(pendingTeamWonId!, categories);
    } else {
      // Normal rally
      await commitRally(pendingTeamWonId!, categories);
    }
  };

  // Commit the rally
  const commitRally = async (
    winningTeamId: string,
    categories: CategoryAssignment[] = []
  ) => {
    setIsSubmitting(true);
    setError(null);

    try {
      await onRecordRally(winningTeamId, categories);

      // Server selection is handled reactively: the useEffect watching
      // serverSelectionNeeded opens the modal once recomputeAndApply settles.
      // Do NOT check stale pre-rally matchState here.

      // Reset pending state for next rally
      setPendingTeamWonId(null);
    } catch (err) {
      setError({
        id: 'rally-error',
        title: 'Rally Record Failed',
        message: err instanceof Error ? err.message : 'Failed to record rally.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Commit a server fault rally - serving team wins point but service transfers
  const commitServerFault = async (
    winningTeamId: string,
    categories: CategoryAssignment[] = []
  ) => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Server fault: the serving team gets a point, but service transfers to opponent
      // Pass isServerFault=true to recordRally
      await onRecordRally(winningTeamId, categories, true);

      // Reset pending state for next rally
      setPendingTeamWonId(null);
    } catch (err) {
      setError({
        id: 'server-fault-error',
        title: 'Server Fault Record Failed',
        message: err instanceof Error ? err.message : 'Failed to record server fault.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle server selection
  const handleServerSelect = async (player: Player) => {
    setIsSubmitting(true);
    setError(null);

    try {
      await onSelectNextServer(player);
      setShowServerSelection(false);
    } catch (err) {
      setError({
        id: 'server-error',
        title: 'ROTATION VIOLATION',
        message: err instanceof Error ? err.message : 'Invalid server selection.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle undo
  const handleUndoRallyClick = () => {
    setUndoType('rally');
    setShowUndoConfirm(true);
  };

  const handleUndoServerSelectionClick = () => {
    setUndoType('server');
    setShowUndoConfirm(true);
  };

  const handleUndoConfirm = async () => {
    setShowUndoConfirm(false);
    setIsSubmitting(true);
    try {
      if (undoType === 'server') {
        await onUndoServerSelection();
        if (!readOnly) setShowServerSelection(true);
      } else {
        await onUndo();
      }
    } catch (err) {
      setError({
        id: 'undo-error',
        title: 'Undo Failed',
        message: undoType === 'server'
          ? 'Could not undo server selection.'
          : 'Could not undo the last rally.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUndoCancel = () => {
    setShowUndoConfirm(false);
  };

  // Handle start next set
  const handleStartNextSet = async (firstServerId: string, firstServerTeamId?: string) => {
    setIsSubmitting(true);
    try {
      await onStartNewSet(firstServerId, firstServerTeamId);
    } catch (err) {
      setError({
        id: 'set-error',
        title: 'Failed to Start Set',
        message: 'Could not start the next set.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Dismiss error popup
  const dismissError = () => {
    setError(null);
  };

  // Cancel volleyball category selection
  const cancelCategories = () => {
    setShowVolleyballCategories(false);
    setPendingTeamWonId(null);
  };


  // Open rally history for the current (in-progress) set
  const openCurrentSetHistory = () => {
    if (!matchState.currentSet || !matchState.teamA || !matchState.teamB) return;
    const setRecord = matchState.sets.find(s => s.endedAt === null);
    if (!setRecord) return;
    setHistoryModal({
      setNumber: matchState.currentSetNumber,
      setRecord,
      rallies: matchState.currentSet.rallies,
    });
  };

  // Open rally history for a completed set (fetches from DB)
  const openPastSetHistory = async (set: SetRecord) => {
    setHistoryLoading(set.id);
    try {
      const rallies = await onFetchSetRallies(set.id);
      setHistoryModal({ setNumber: set.setNumber, setRecord: set, rallies });
    } catch {
      // silently fail — no state corruption
    } finally {
      setHistoryLoading(null);
    }
  };

  // Handle serve foul — records rally + service break automatically, modal stays open for opponent
  const handleServeFoul = async (player: Player) => {
    setIsSubmitting(true);
    setError(null);
    try {
      await onRecordServeFoul(player);
      // showServerSelection stays true; after state updates servingTeam becomes the opponent,
      // so the modal re-renders to show the opponent's server selection automatically.
    } catch (err) {
      setError({
        id: 'serve-foul-error',
        title: 'Serve Foul Failed',
        message: err instanceof Error ? err.message : 'Failed to record serve foul.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle server fault — serving team wins rally but different player serves (fault)
  const handleServerFault = async () => {
    if (!onRecordServerFault) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await onRecordServerFault();
    } catch (err) {
      setError({
        id: 'server-fault-error',
        title: 'Server Fault Failed',
        message: err instanceof Error ? err.message : 'Failed to record server fault.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle clicking TIME OUT button
  const handleTimeoutClick = () => {
    setShowTimeoutModal(true);
  };

  // Check if in timeout mode
  const inTimeout = !!activeTimeout;

  // Get timeout team info
  const timeoutTeam = activeTimeout && matchState.teamA && matchState.teamB
    ? (activeTimeout.teamId === matchState.teamA.id ? matchState.teamA : matchState.teamB)
    : null;

  // Auto-open timeout modal when an active timeout is detected (recovery scenario)
  useEffect(() => {
    if (inTimeout && !showTimeoutModal && onStartTimeout && onEndTimeout) {
      setShowTimeoutModal(true);
    }
  }, [inTimeout]);

  if (!matchState.match || !matchState.teamA || !matchState.teamB || !matchState.currentSet) {
    console.error('[LiveScoring] render check failed:', {
      hasMatch: !!matchState.match,
      hasTeamA: !!matchState.teamA,
      hasTeamB: !!matchState.teamB,
      hasCurrentSet: !!matchState.currentSet,
      status: matchState.status,
      currentSetNumber: matchState.currentSetNumber,
      setsCount: matchState.sets?.length ?? 0,
    });

    // Show recoverable error instead of infinite loading
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-error/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-text-primary mb-2">Unable to Load Match</h1>
          <p className="text-text-secondary mb-4 text-sm">
            The match data could not be recovered. This may happen if the match was modified on another device.
          </p>
          <div className="text-xs text-text-muted mb-6 p-3 bg-surface-raised rounded-lg text-left">
            <p>Debug info:</p>
            <p>Status: {matchState.status}</p>
            <p>Match: {matchState.match ? 'loaded' : 'missing'}</p>
            <p>Team A: {matchState.teamA ? 'loaded' : 'missing'}</p>
            <p>Team B: {matchState.teamB ? 'loaded' : 'missing'}</p>
            <p>Current Set: {matchState.currentSet ? 'loaded' : 'missing'}</p>
            <p>Current Set #: {matchState.currentSetNumber}</p>
            <p>Sets in DB: {matchState.sets?.length ?? 0}</p>
          </div>
          <div className="space-y-3">
            <button
              onClick={() => {
                window.location.reload();
              }}
              className="w-full py-3 px-6 rounded-lg font-bold bg-primary text-white hover:opacity-90 transition-all"
            >
              Retry Recovery
            </button>
            {tournamentId && (
              <button
                onClick={() => navigate(`/tournament/${tournamentId}`)}
                className="w-full py-3 px-6 rounded-lg font-semibold bg-surface border border-border text-text-primary hover:bg-surface-raised transition-all"
              >
                Back to Tournament
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Destructure for type narrowing
  const { teamA, teamB, currentSet, match, currentSetNumber } = matchState;

  // Determine winning team for modals
  const winningTeam = pendingTeamWonId === teamA.id ? teamA : teamB;

  // Timeout counts per team (match-level, not per set)
  const firstTeamTimeouts = completedTimeouts.filter(t => t.teamId === firstTeam.id).length;
  const secondTeamTimeouts = completedTimeouts.filter(t => t.teamId === secondTeam.id).length;

  return (
    <div className="h-screen bg-background flex flex-col overflow-hidden" style={{ height: '100dvh' }}>
      {/* App Header with tournament context */}
      <AppHeader
        onSignOut={onSignOut}
        title={tournamentName || `Match #${match.matchNumber}`}
        subtitle={tournamentName ? `${match.matchType.charAt(0).toUpperCase() + match.matchType.slice(1)} Match #${match.matchNumber}` : match.date}
        onNavigate={tournamentId && onExitToTournament ? () => {
          if (readOnly) { onExitToTournament(tournamentId); }
          else { setShowExitConfirm(true); }
        } : undefined}
        navIcon={tournamentId && onExitToTournament ? 'tournaments' : undefined}
        tournamentId={tournamentId}
        tournamentName={tournamentName ?? undefined}
        rightExtra={
          <button
            onClick={toggleFullscreen}
            title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            className="p-1.5 rounded text-text-muted hover:text-text-primary transition-colors"
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          >
            {isFullscreen ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 3v3a2 2 0 0 1-2 2H3"/><path d="M21 8h-3a2 2 0 0 1-2-2V3"/>
                <path d="M3 16h3a2 2 0 0 1 2 2v3"/><path d="M16 21v-3a2 2 0 0 1 2-2h3"/>
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/>
                <path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/>
              </svg>
            )}
          </button>
        }
      />

      {/* Match Meta Strip */}
      <div className="bg-surface border-b border-border px-4 py-1.5">
        <div className="flex items-center justify-between text-xs text-text-muted">
          <span>MATCH #{match.matchNumber}</span>
          <span>{match.date}</span>
          <span className="uppercase">{match.matchType}</span>
        </div>
      </div>

      {/* Viewer Refresh Bar */}
      {readOnly && onRefresh && (
        <div className="bg-info/5 border-b border-info/20 px-4 py-2 flex items-center justify-between">
          <span className="text-xs text-text-muted">
            {lastRefreshed ? `Updated ${formatTime(lastRefreshed)}` : 'Auto-refreshing...'}
          </span>
          <button
            onClick={handleManualRefresh}
            disabled={refreshing}
            className="flex items-center gap-1.5 text-xs font-semibold text-info hover:text-info/80 transition-colors disabled:opacity-50"
          >
            <svg
              width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
              className={refreshing ? 'animate-spin' : ''}
            >
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      )}

      {/* Current Set Indicator */}
      <div className="bg-surface-raised border-b border-border px-4 py-1.5">
        <div className="flex items-center justify-center gap-2">
          <span className="w-2 h-2 rounded-full bg-serving pulse-serve" />
          <span className="text-sm font-bold tracking-wide text-text-primary uppercase">
            Set {currentSetNumber} of {MATCH_CONFIG[match.matchType].maxSets}
          </span>
          <span className="text-xs text-text-muted">·</span>
          <span className="text-xs font-semibold uppercase text-text-secondary">
            {teamA.setsWon}–{teamB.setsWon} sets
          </span>
        </div>
      </div>

      {/* Exit to Tournament Confirmation */}
      {showExitConfirm && !readOnly && tournamentId && onExitToTournament && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <h3 className="text-base font-bold text-text-primary mb-1">Match in Progress</h3>
            <p className="text-sm text-text-secondary mb-5">
              What would you like to do?
            </p>
            <div className="space-y-2">
              <button
                onClick={() => setShowExitConfirm(false)}
                className="w-full py-3 px-4 rounded-lg font-bold text-sm bg-success text-white transition-all active:scale-[0.98]"
              >
                CONTINUE SCORING
              </button>
              <button
                onClick={() => { setShowExitConfirm(false); onExitToTournament(tournamentId); }}
                className="w-full py-3 px-4 rounded-lg font-semibold text-sm bg-surface border border-border text-text-primary transition-all active:scale-[0.98] hover:bg-surface-raised"
              >
                SAVE AND EXIT
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scoreboard — serving team first */}
      <div className="bg-surface border-b border-border">
        <div className="grid grid-cols-2 divide-x divide-border">
          {/* Serving Team Score (first) */}
          <div className="py-2 px-2 sm:px-4">
            <div className="flex items-center gap-2 mb-0.5">
              {/* Timeout count at outer left edge */}
              <span
                className="text-xs font-bold text-white flex-shrink-0"
                title="Timeouts taken"
              >
                T{firstTeamTimeouts}
              </span>
              <div className="flex-1 flex items-center justify-center gap-2">
                {firstTeam.logoUrl ? (
                  <img
                    src={firstTeam.logoUrl}
                    alt={`${firstTeam.shortName} logo`}
                    className="w-8 h-8 rounded object-contain bg-surface-raised p-0.5"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const fallback = e.currentTarget.nextElementSibling;
                      if (fallback) {
                        (fallback as HTMLElement).style.display = 'flex';
                      }
                    }}
                  />
                ) : null}
                <div
                  className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${firstTeam.logoUrl ? 'hidden' : ''}`}
                  style={{ backgroundColor: firstTeam.color }}
                >
                  <span className="text-white text-sm font-bold">{firstTeam.shortName?.charAt(0) || '?'}</span>
                </div>
                <span className="text-base font-bold uppercase" style={{ color: firstTeam.color }}>
                  {firstTeam.shortName}
                </span>
                {servingTeam && (
                  <span className="w-2 h-2 rounded-full bg-serving pulse-serve" title="Serving" />
                )}
              </div>
            </div>
            <p className="font-mono-nums text-5xl font-bold text-center text-text-primary">
              {String(firstScore).padStart(2, '0')}
            </p>
            <div className="flex justify-center gap-1 mt-2">
              {Array.from({ length: MATCH_CONFIG[match.matchType].maxSets }).map((_, i) => (
                <span
                  key={i}
                  className={`w-2.5 h-2.5 rounded-full ${i < firstTeam.setsWon ? '' : 'bg-border'}`}
                  style={i < firstTeam.setsWon ? { backgroundColor: firstTeam.color } : {}}
                />
              ))}
            </div>
          </div>

          {/* Receiving Team Score (second) */}
          <div className="py-2 px-2 sm:px-4">
            <div className="flex items-center gap-2 mb-0.5">
              <div className="flex-1 flex items-center justify-center gap-2">
                <span className="text-base font-bold uppercase" style={{ color: secondTeam.color }}>
                  {secondTeam.shortName}
                </span>
                {secondTeam.logoUrl ? (
                  <img
                    src={secondTeam.logoUrl}
                    alt={`${secondTeam.shortName} logo`}
                    className="w-8 h-8 rounded object-contain bg-surface-raised p-0.5"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const fallback = e.currentTarget.nextElementSibling;
                      if (fallback) {
                        (fallback as HTMLElement).style.display = 'flex';
                      }
                    }}
                  />
                ) : null}
                <div
                  className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${secondTeam.logoUrl ? 'hidden' : ''}`}
                  style={{ backgroundColor: secondTeam.color }}
                >
                  <span className="text-white text-sm font-bold">{secondTeam.shortName?.charAt(0) || '?'}</span>
                </div>
              </div>
              {/* Timeout count at outer right edge */}
              <span
                className="text-xs font-bold text-white flex-shrink-0"
                title="Timeouts taken"
              >
                T{secondTeamTimeouts}
              </span>
            </div>
            <p className="font-mono-nums text-5xl font-bold text-center text-text-primary">
              {String(secondScore).padStart(2, '0')}
            </p>
            <div className="flex justify-center gap-1 mt-2">
              {Array.from({ length: MATCH_CONFIG[match.matchType].maxSets }).map((_, i) => (
                <span
                  key={i}
                  className={`w-2.5 h-2.5 rounded-full ${i < secondTeam.setsWon ? '' : 'bg-border'}`}
                  style={i < secondTeam.setsWon ? { backgroundColor: secondTeam.color } : {}}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Serving Panel */}
      {servingTeam && currentServer && !showSetWonBanner && (
        <div
          className={`mx-3 mt-2 rounded-lg p-3 border transition-all ${
            servingTeam.id === teamA.id ? 'glow-team-a border-team-a' : 'glow-team-b border-team-b'
          }`}
          style={{ backgroundColor: `${servingTeam.color}10` }}
        >
          <div className="flex items-center gap-3">
            {/* Left side: Serving info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="w-2.5 h-2.5 rounded-full bg-serving pulse-serve" />
                <span className="text-xs font-semibold uppercase text-serving">Serving Now</span>
                {currentSet.goldenPoint && (
                  <span className="ml-auto px-2 py-0.5 bg-warning text-black text-xs font-bold rounded">
                    GOLDEN POINT
                  </span>
                )}
              </div>
              <p
                className="text-sm font-bold leading-tight"
                style={{ color: servingTeam.color }}
              >
                {servingTeam.name.toUpperCase()}
              </p>
              <p className="text-xl font-bold text-text-primary leading-tight">{currentServer.name}</p>
              <div className="flex items-center gap-1 mt-1.5">
                <span className="text-xs text-text-muted mr-1">ROT</span>
                {rotationDots.map((dot, i) => (
                  <span
                    key={i}
                    className={`w-2.5 h-2.5 rounded-full border ${
                      dot.filled
                        ? dot.isCurrent
                          ? 'bg-serving border-serving'
                          : 'bg-text-muted border-text-muted'
                        : 'bg-transparent border-border'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Right side: Team Logo */}
            <div className="flex-shrink-0">
              {servingTeam.logoUrl ? (
                <img
                  src={servingTeam.logoUrl}
                  alt={`${servingTeam.shortName} logo`}
                  className="w-14 h-14 rounded-lg object-contain bg-surface p-1"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling;
                    if (fallback) {
                      (fallback as HTMLElement).style.display = 'flex';
                    }
                  }}
                />
              ) : null}
              <div
                className={`w-14 h-14 rounded-lg flex items-center justify-center ${servingTeam.logoUrl ? 'hidden' : ''}`}
                style={{ backgroundColor: servingTeam.color }}
              >
                <span className="text-white text-xl font-bold">
                  {servingTeam.shortName?.charAt(0) || '?'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content Area with Rally Buttons and Recent Rallies */}
      <div className="flex-1 overflow-y-auto overscroll-contain px-3 py-3">
        {/* Timeout completed notification */}
        {lastTimeoutDuration && timeoutTeam && (
          <div className="mb-4">
            <TimeoutCompletedDisplay team={timeoutTeam} durationSeconds={lastTimeoutDuration} />
          </div>
        )}

        {/* Rally Buttons */}
        {!readOnly && !showSetWonBanner && !inTimeout && (
          <div className="space-y-2.5 mb-4">
            <button
              onClick={() => handleTeamWonClick(teamA.id)}
              disabled={isSubmitting}
              className="btn-primary text-white font-bold py-4"
              style={{ backgroundColor: teamA.color }}
            >
              <span className="block text-xs opacity-80">{teamA.name}</span>
              <span className="text-base">WON THIS RALLY</span>
            </button>
            <button
              onClick={() => handleTeamWonClick(teamB.id)}
              disabled={isSubmitting}
              className="btn-primary text-white font-bold py-4"
              style={{ backgroundColor: teamB.color }}
            >
              <span className="block text-xs opacity-80">{teamB.name}</span>
              <span className="text-base">WON THIS RALLY</span>
            </button>
          </div>
        )}


        {/* Recent Rallies */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold uppercase text-text-muted">Recent Rallies</h3>
            {currentSet.rallies.length > 0 && (
              <button
                onClick={openCurrentSetHistory}
                className="text-xs text-text-secondary hover:text-text-primary transition-colors"
              >
                View All ({currentSet.rallies.length})
              </button>
            )}
          </div>
          <div className="bg-surface rounded-lg border border-border overflow-hidden">
            {currentSet.rallies.length === 0 ? (
              <p className="text-text-muted text-sm p-3 text-center">No rallies recorded yet</p>
            ) : (() => {
              const allPlayers = [...teamA.players, ...teamB.players];
              const ascending = [...currentSet.rallies].reverse();
              const runningScores: { a: number; b: number }[] = [];
              let rA = 0, rB = 0;
              for (const r of ascending) {
                if (r.pointWonById === teamA.id) rA++;
                else if (r.pointWonById === teamB.id) rB++;
                runningScores.push({ a: rA, b: rB });
              }
              return currentSet.rallies.slice(0, 5).map((rally, index) => {
                const rallyServingTeam = rally.servingTeamId === teamA.id ? teamA : teamB;
                const rallyWinningTeam = rally.pointWonById === teamA.id ? teamA : teamB;
                const servingTeamName = rallyServingTeam.shortName;
                const winningTeamName = rallyWinningTeam.shortName;
                const server = allPlayers.find(p => p.id === rally.serverId);
                const scoreIdx = runningScores.length - 1 - index;
                const score = runningScores[scoreIdx];
                const isServeFoul = rally.lostReason === 'FOUL';
                const isServerFault = rally.isServerFault;

                // Format categories for display with team colors
                const formatCategoryDisplay = (categories?: CategoryAssignment[]): React.ReactNode => {
                  if (!categories || categories.length === 0) return null;
                  return categories.map(c => {
                    const player = allPlayers.find(p => p.id === c.playerId);
                    const playerTeam = teamA.players.some(p => p.id === c.playerId) ? teamA : teamB;
                    const firstName = player?.name?.split(' ')[0] ?? '?';
                    return (
                      <span key={`${c.category}-${c.playerId}`} style={{ color: playerTeam.color }} className="font-medium">
                        {c.category}:{firstName}
                      </span>
                    );
                  });
                };
                const categoriesDisplay = formatCategoryDisplay(rally.categories);

                return (
                  <div
                    key={rally.id}
                    className={`px-3 py-2 text-xs ${
                      isServeFoul ? 'bg-warning/10' : isServerFault ? 'bg-error/10' : index !== 0 ? 'border-t border-border' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-text-muted w-6">S{currentSetNumber}</span>
                      <span
                        className="font-semibold w-8"
                        style={{ color: rally.servingTeamId === teamA.id ? teamA.color : teamB.color }}
                      >
                        {servingTeamName}
                      </span>
                      <span className="text-text-secondary truncate flex-1">
                        {isServeFoul ? (
                          <span className="text-warning font-semibold">SF: {server?.name}</span>
                        ) : isServerFault ? (
                          <span className="text-error font-semibold">FAULT: {server?.name}</span>
                        ) : (
                          server?.name
                        )}
                      </span>
                      <span className="text-text-muted">→</span>
                      <span
                        className="font-semibold w-8"
                        style={{ color: rally.pointWonById === teamA.id ? teamA.color : teamB.color }}
                      >
                        {winningTeamName}
                      </span>
                      {score && (
                        <span className="font-mono font-bold text-[11px] ml-auto pl-2 shrink-0">
                          <span style={{ color: teamA.color }}>{score.a}</span>
                          <span className="text-text-muted">–</span>
                          <span style={{ color: teamB.color }}>{score.b}</span>
                        </span>
                      )}
                    </div>
                    {categoriesDisplay && (
                      <div className="mt-1 text-[10px] pl-14 flex flex-wrap gap-x-2 truncate">
                        {categoriesDisplay}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </div>

        {/* Completed Sets + Current Set Indicator */}
        <div className="mt-4">
          <h3 className="text-xs font-semibold uppercase text-text-muted mb-2">Set Progress</h3>
          <div className="space-y-1.5">
            {/* Completed sets */}
            {matchState.sets
              .filter(s => s.endedAt !== null)
              .sort((a, b) => a.setNumber - b.setNumber)
              .map(set => {
                const winner = set.winnerId === teamA.id ? teamA : teamB;
                const loser = set.winnerId === teamA.id ? teamB : teamA;
                const winnerScore = set.winnerId === teamA.id ? set.scoreA : set.scoreB;
                const loserScore = set.winnerId === teamA.id ? set.scoreB : set.scoreA;
                const isLoading = historyLoading === set.id;
                return (
                  <button
                    key={set.id}
                    onClick={() => !isLoading && openPastSetHistory(set)}
                    disabled={isLoading}
                    className="w-full flex items-center gap-2 px-3 py-2 bg-surface border border-border rounded-lg hover:border-text-muted transition-colors text-xs disabled:opacity-60"
                  >
                    <span className="text-success font-bold flex-shrink-0">✓</span>
                    <span className="text-text-muted font-semibold flex-shrink-0">Set {set.setNumber}</span>
                    <span
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: winner.color }}
                      title={winner.name}
                    />
                    <span className="font-bold flex-shrink-0" style={{ color: winner.color }}>
                      {winner.shortName}
                    </span>
                    <span className="font-mono-nums font-semibold text-text-primary">
                      {winnerScore}–{loserScore}
                    </span>
                    <span
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: loser.color }}
                      title={loser.name}
                    />
                    <span className="font-medium text-text-secondary flex-shrink-0">
                      {loser.shortName}
                    </span>
                    <span className="flex-1" />
                    {isLoading ? (
                      <span className="w-4 h-4 border-2 border-border border-t-text-secondary rounded-full animate-spin" />
                    ) : (
                      <span className="text-text-muted">›</span>
                    )}
                  </button>
                );
              })}

            {/* Current set in progress */}
            <div className="flex items-center gap-2 px-3 py-2 bg-surface-raised border border-serving/40 rounded-lg text-xs animate-fade-in">
              <span className="text-serving font-bold flex-shrink-0">▶</span>
              <span className="text-text-muted font-semibold flex-shrink-0">Set {currentSetNumber}</span>
              <span className="text-text-secondary">In Progress</span>
              <span className="flex-1" />
              <span
                className="w-4 h-4 rounded-full flex-shrink-0"
                style={{ backgroundColor: teamA.color }}
                title={teamA.name}
              />
              <span className="font-mono-nums font-semibold text-text-primary">
                {currentSet.scoreA}–{currentSet.scoreB}
              </span>
              <span
                className="w-4 h-4 rounded-full flex-shrink-0"
                style={{ backgroundColor: teamB.color }}
                title={teamB.name}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Footer - Undo Actions + Timeout Link */}
      {!readOnly && !showSetWonBanner && (
        <div className="bg-surface border-t border-border px-3 py-2" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 8px)' }}>
          <div className="flex items-center gap-3">
            <button
              onClick={handleUndoRallyClick}
              disabled={isSubmitting || currentSet.rallies.length === 0}
              className="text-xs text-error font-semibold disabled:opacity-50 py-1"
            >
              Undo Rally
            </button>
            <button
              onClick={handleUndoServerSelectionClick}
              disabled={isSubmitting || !canUndoServerSelection().canUndo}
              className="text-xs text-warning font-semibold disabled:opacity-50 py-1"
            >
              Undo Server
            </button>
            <div className="flex-1" />
            <button
              onClick={handleTimeoutClick}
              disabled={isSubmitting || !onStartTimeout}
              className="text-xs text-text-secondary font-semibold disabled:opacity-50 hover:text-text-primary transition-colors py-1"
            >
              TIMEOUT
            </button>
          </div>
        </div>
      )}

      {/* Volleyball Category Selection */}
      {showVolleyballCategories && pendingTeamWonId && winningTeam && losingTeam && (
        <VolleyballCategoryModal
          winningTeam={winningTeam}
          losingTeam={losingTeam}
          servingTeamId={matchState.currentSet?.servingTeamId || ''}
          currentServerId={currentServer?.id || null}
          servingTeam={servingTeam}
          onConfirm={handleCategoryConfirm}
          onCancel={cancelCategories}
          teamA={teamA}
          teamB={teamB}
          currentSetNumber={currentSetNumber}
          matchType={match.matchType}
          currentScoreA={currentSet.scoreA}
          currentScoreB={currentSet.scoreB}
          completedSets={matchState.sets}
          completedTimeouts={completedTimeouts}
        />
      )}

      {/* Server Selection Modal */}
      {/* Do not show during set-won: the SetWonBanner handles server selection for the new set */}
      {showServerSelection && servingTeam && !readOnly && !showSetWonBanner && (
        <ServerSelectionModal
          team={servingTeam}
          eligibleServers={eligibleServers}
          onSelect={handleServerSelect}
          reason="service-break"
          onServeFoul={handleServeFoul}
          onServerFault={handleServerFault}
          currentServer={currentServer}
          rotationCooloff={matchState.serveRotationCooloff}
          onTimeout={onStartTimeout ? handleTimeoutClick : undefined}
          teamA={teamA}
          teamB={teamB}
          currentSetNumber={currentSetNumber}
          matchType={match.matchType}
          currentScoreA={currentSet.scoreA}
          currentScoreB={currentSet.scoreB}
          completedSets={matchState.sets}
          completedTimeouts={completedTimeouts}
        />
      )}

      {/* Set Won Banner */}
      {showSetWonBanner && matchState.currentSet && !readOnly && (
        <SetWonBanner
          matchState={matchState}
          onStartNextSet={handleStartNextSet}
          onSelectFirstServerTeam={onSelectFirstServerTeam}
          completedTimeouts={completedTimeouts}
        />
      )}

      {/* Error Popup */}
      {error && (
        <ErrorPopup
          title={error.title}
          message={error.message}
          onDismiss={dismissError}
        />
      )}

      {/* Undo Confirmation Popup */}
      {showUndoConfirm && (
        <UndoConfirmPopup
          undoType={undoType}
          onConfirm={handleUndoConfirm}
          onCancel={handleUndoCancel}
        />
      )}

      {/* Rally History Modal */}
      {historyModal && matchState.teamA && matchState.teamB && (
        <RallyHistoryModal
          setNumber={historyModal.setNumber}
          setRecord={historyModal.setRecord}
          rallies={historyModal.rallies}
          teamA={matchState.teamA}
          teamB={matchState.teamB}
          onClose={() => setHistoryModal(null)}
        />
      )}

      {/* Timeout Modal */}
      {showTimeoutModal && matchState.teamA && matchState.teamB && onStartTimeout && onEndTimeout && (
        <TimeoutModal
          isOpen={showTimeoutModal}
          teamA={matchState.teamA}
          teamB={matchState.teamB}
          setNumber={currentSetNumber}
          activeTimeoutId={activeTimeout?.id ?? null}
          activeTimeoutTeamId={activeTimeout?.teamId ?? null}
          activeTimeoutStartTime={activeTimeout?.startedAt ?? null}
          onStartTimeout={onStartTimeout}
          onEndTimeout={onEndTimeout}
          onClose={() => setShowTimeoutModal(false)}
          completedTimeouts={completedTimeouts}
        />
      )}

      {/* Side Change Notice */}
      {showSideChangeNotice && onConfirmSideChange && (
        <SideChangeNotice
          isOpen={showSideChangeNotice}
          winningPoint={getMatchRulesConfigLocal(matchState.match, matchState.tournamentRules)?.winningPoint ?? 15}
          onConfirm={onConfirmSideChange}
        />
      )}
    </div>
  );
}
